import { useTheme } from "@/context/ThemeContext";

/**
 * ClientFlow brand marks.
 *
 * <Logo />        — full wordmark (CF + "ClientFlow"). Use in header/login/landing.
 * <LogoMark />    — square CF icon mark only. Use in tight spots (sidebar, mobile nav, avatars).
 *
 * Both components forward className/style for sizing. Default heights are sensible
 * for a 16-h navbar; override with `className="h-10"` etc. as needed.
 *
 * Light variant: dark wordmark on light backgrounds (deep-pine letters).
 * Dark variant: silver/white wordmark with a gold accent for dark backgrounds.
 *
 * Why both variants are rendered simultaneously: swapping the <img> `src` on
 * theme toggle causes a visible flash while the browser fetches+decodes the
 * other PNG (each ~100–370 KB). Stacking both and cross-fading via opacity
 * keeps both bitmaps in the DOM, so the swap is instant on every toggle.
 */
const SwapImg = ({ lightSrc, darkSrc, className, alt, testid, objectClass = "" }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  return (
    <span className={`relative inline-block ${className} select-none align-middle`} data-testid={testid}>
      <img
        src={lightSrc}
        alt={alt}
        draggable="false"
        decoding="async"
        fetchPriority="high"
        className={`block w-full h-full ${objectClass} transition-opacity duration-150 ease-out ${isDark ? "opacity-0" : "opacity-100"}`}
      />
      <img
        src={darkSrc}
        alt=""
        aria-hidden="true"
        draggable="false"
        decoding="async"
        fetchPriority="high"
        className={`absolute inset-0 w-full h-full ${objectClass} transition-opacity duration-150 ease-out ${isDark ? "opacity-100" : "opacity-0"}`}
      />
    </span>
  );
};

export const Logo = ({ className = "h-8 w-auto", alt = "ClientFlow" }) => (
  <SwapImg
    lightSrc="/clientflow-logo.png"
    darkSrc="/clientflow-logo-dark.png"
    className={className}
    alt={alt}
    testid="brand-logo"
  />
);

export const LogoMark = ({ className = "w-9 h-9", alt = "ClientFlow" }) => (
  <SwapImg
    lightSrc="/clientflow-mark.png"
    darkSrc="/clientflow-mark-dark.png"
    className={className}
    alt={alt}
    testid="brand-logo-mark"
    objectClass="object-contain"
  />
);

export default Logo;
